/**
 * OnboardingIncompleteBanner — Phase 1.32 (2026-06-29).
 *
 * Persistent banner shown across all authenticated shells when the
 * signed-in owner has not yet completed onboarding (sys__account_user
 * .onboarding_completed_at IS NULL). Mirrors EmailVerificationBanner's
 * shape: non-dismissible, single CTA, self-hides once the underlying
 * state flips.
 *
 * Why this exists alongside the UserShell routing gate (which already
 * redirects first-time owners to /welcome): the redirect catches the
 * normal path. The banner is the safety net for edge cases — manual URL
 * navigation, stale tabs, post-cache-clear states, or any future path
 * that lands an owner on /home before they've finished setup. Friction-
 * free for completed users (renders nothing); explicit nudge for the rest.
 *
 * Per Ean's framing (Phase 1.32): bootstrap no longer pre-installs
 * Haiku. If a user lands here without finishing the wizard, chat has
 * NO models installed. This banner is the most prominent
 * "go finish your setup" affordance + the existing /welcome route is
 * the destination.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useSession } from '../hooks/useSession.tsx';
import { fetchOnboardingState } from '../lib/api.ts';

/**
 * Module-scope cache so re-mounts inside the same session don't re-fetch.
 * Cleared when user_id changes.
 */
const cache: { userId: string | null; completed: boolean | null } = {
  userId: null,
  completed: null,
};

/** Called by Welcome.tsx after Finish so this banner stops rendering
 *  on the next /home mount without waiting for a server roundtrip. */
export function markOnboardingCompletedLocallyForBanner(userId: string): void {
  cache.userId = userId;
  cache.completed = true;
}

export function OnboardingIncompleteBanner() {
  const { user } = useSession();
  const navigate = useNavigate();
  const [completed, setCompleted] = useState<boolean | null>(
    cache.userId === (user?.user_id ?? null) ? cache.completed : null,
  );

  useEffect(() => {
    if (user === null) return;
    // Only owners hit the wizard. Skip the fetch for admin/user roles —
    // they're never blocked by onboarding state.
    if (user.role !== 'owner') {
      cache.userId = user.user_id;
      cache.completed = true;
      setCompleted(true);
      return;
    }
    // Cache hit?
    if (cache.userId === user.user_id && cache.completed !== null) {
      setCompleted(cache.completed);
      return;
    }
    let cancelled = false;
    fetchOnboardingState()
      .then((state) => {
        if (cancelled) return;
        const done = state.onboarding_completed_at !== null;
        cache.userId = user.user_id;
        cache.completed = done;
        setCompleted(done);
      })
      .catch(() => {
        // Fail open — assume completed if we can't read the state.
        if (cancelled) return;
        cache.userId = user.user_id;
        cache.completed = true;
        setCompleted(true);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  // Render nothing while probing OR if the user has completed OR if
  // they're a non-owner role. Phase 1.21 EmailVerificationBanner uses
  // the same "render-nothing-until-known" pattern so the banner doesn't
  // flicker in for half a frame on a healthy session.
  if (user === null) return null;
  if (completed === null) return null;
  if (completed === true) return null;
  // Don't render the banner ON the wizard itself — the wizard IS the
  // resolution path; double-banner would be noisy.
  if (typeof window !== 'undefined' && window.location.pathname === '/welcome') {
    return null;
  }

  return (
    <div className="verify-banner" role="alert" aria-live="polite">
      <div className="verify-banner-text">
        <strong>Finish setting up your workspace</strong>
        <span className="verify-banner-subtext">
          A few quick picks (models, connectors, prompts) and you're ready to chat.
        </span>
      </div>
      <div className="verify-banner-form">
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={() => navigate('/welcome')}
        >
          Finish setup
        </button>
      </div>
    </div>
  );
}
